import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Clock, Users, Leaf } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: Leaf, text: "Walk the garlic beds, bean trellises, and okra patch" },
  { icon: Clock, text: "Roughly 90 unhurried minutes, golden hour slots available" },
  { icon: Users, text: "Small groups only — families, food lovers, and chefs welcome" },
];

export default function Tours() {
  const [form, setForm] = useState({ name: "", email: "", date: "", party_size: 2, requests: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/tours`, { ...form, party_size: Number(form.party_size) });
      setBooked(data);
      toast.success("Tour request submitted", { description: "We'll confirm your date by email." });
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "bg-obsidian border border-line rounded-md px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus-ring w-full";

  return (
    <main data-testid="tours-page" className="pt-40 pb-32">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6"
        >
          <p className="overline-tag mb-6">Farm Tour Bookings</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-light text-zinc-100 leading-[1.05]">
            Come pull a bulb <span className="italic text-saffron">out of the earth</span>
          </h1>
          <p className="mt-8 text-base leading-relaxed text-zinc-400 max-w-lg">
            A slow wander through the fields with the family that farms them. You'll smell the garlic
            before you see it, learn why we grow the strange stuff, and taste whatever the season is
            showing off.
          </p>
          <ul className="mt-12 flex flex-col gap-6">
            {PERKS.map((p) => (
              <li key={p.text} className="flex items-start gap-4">
                <p.icon className="w-5 h-5 text-saffron mt-0.5 shrink-0" />
                <span className="text-sm text-zinc-300 leading-relaxed">{p.text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 text-xs uppercase tracking-[0.2em] text-zinc-600">
            Tours run June through October · Weather permitting · Boots recommended
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-6"
        >
          {booked ? (
            <div
              data-testid="tour-confirmation"
              className="border border-line bg-surface p-10 flex flex-col items-start gap-6"
            >
              <CheckCircle2 className="w-12 h-12 text-saffron" />
              <p className="font-display text-3xl text-zinc-100">See you in the rows.</p>
              <p className="text-zinc-400 leading-relaxed text-sm">
                Your request for <span className="text-zinc-100">{booked.date}</span> (party of{" "}
                {booked.party_size}) is in. We'll confirm at{" "}
                <span className="text-zinc-100">{booked.email}</span> within a day or two — we check
                email between chores.
              </p>
              <button
                data-testid="tour-book-another-button"
                onClick={() => {
                  setBooked(null);
                  setForm({ name: "", email: "", date: "", party_size: 2, requests: "" });
                }}
                className="rounded-full border border-saffron/50 px-8 py-3 text-xs uppercase tracking-[0.2em] font-bold text-saffron hover:bg-saffron hover:text-obsidian transition-colors focus-ring"
              >
                Book Another Tour
              </button>
            </div>
          ) : (
            <form
              data-testid="tour-booking-form"
              onSubmit={submit}
              className="border border-line bg-surface p-10 flex flex-col gap-5"
            >
              <p className="overline-tag">Request a Date</p>
              <input
                data-testid="tour-name-input"
                required
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="tour-email-input"
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
              <div className="grid grid-cols-2 gap-5">
                <input
                  data-testid="tour-date-input"
                  required
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={inputCls}
                />
                <input
                  data-testid="tour-party-size-input"
                  required
                  type="number"
                  min={1}
                  max={12}
                  placeholder="Party size"
                  value={form.party_size}
                  onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                  className={inputCls}
                />
              </div>
              <textarea
                data-testid="tour-requests-input"
                rows={4}
                placeholder="Special requests — mobility needs, chef groups, kids in tow…"
                value={form.requests}
                onChange={(e) => setForm({ ...form, requests: e.target.value })}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="tour-booking-submit"
                disabled={submitting}
                className="mt-2 rounded-full bg-saffron px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold text-obsidian hover:bg-saffron-hover transition-colors disabled:opacity-50 focus-ring"
              >
                {submitting ? "Sending to the farm…" : "Request Tour"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </main>
  );
}
